
import React, { useState, useEffect, useMemo } from 'react';
import { GameType, Tournament } from '../../types';
import { Link, useNavigate } from 'react-router-dom';
import { 
  getCardgames, 
  getAllTournaments, 
  getGameIcon
} from '../../services/supabaseService';
import { useNotification } from '../../components/member/NotificationProvider';

interface TournamentsProps {
  activeGame: number | 'All';
  setActiveGame: (game: number | 'All') => void;
}

const MONTHS_PT = [
  { value: '0', label: 'Janeiro' },
  { value: '1', label: 'Fevereiro' },
  { value: '2', label: 'Março' },
  { value: '3', label: 'Abril' },
  { value: '4', label: 'Maio' },
  { value: '5', label: 'Junho' },
  { value: '6', label: 'Julho' },
  { value: '7', label: 'Agosto' },
  { value: '8', label: 'Setembro' },
  { value: '9', label: 'Outubro' },
  { value: '10', label: 'Novembro' },
  { value: '11', label: 'Dezembro' },
];

export const Tournaments: React.FC<TournamentsProps> = ({ activeGame, setActiveGame }) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [torneios, setTorneios] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dbGames, setDbGames] = useState<any[]>([]);
  const [timeScope, setTimeScope] = useState<'upcoming' | 'all'>('upcoming');
  const [selectedMonth, setSelectedMonth] = useState<string>('All');

  useEffect(() => {
    const fetchMetadata = async () => {
      const games = await getCardgames();
      setDbGames(games);
    };
    fetchMetadata();
  }, []);

  const fetchTourneys = async () => {
    setLoading(true);
    const data = await getAllTournaments();
    if (data) {
      const mapped = data.map((t: any) => {
        const mainTicket = t.tickets?.[0];
        const isSoldOut = mainTicket ? (mainTicket.sold_quantity >= mainTicket.max_quantity) : false;
        const saleEnded = mainTicket ? (new Date() > new Date(mainTicket.sale_end)) : false;
        const saleNotStarted = mainTicket ? (new Date() < new Date(mainTicket.sale_start)) : false;
        const isNotOpen = t.status !== 'open';
        const ticketsAvailable = !isNotOpen && (!mainTicket || (!isSoldOut && !saleEnded && !saleNotStarted));

        // Find store of tournament (direct relation with fallback to creator's store)
        let hostStore = t.store;
        if (!hostStore) {
          const creatorStoreArray = t.creator?.store_members;
          if (Array.isArray(creatorStoreArray) && creatorStoreArray.length > 0) {
            hostStore = creatorStoreArray[0]?.store;
          }
        }

        const rawStatus = (t.status || 'open').toLowerCase();
        let statusLabel = 'Inscrições Abertas';
        let statusBadgeClass = 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
        let statusDotClass = 'bg-emerald-400';

        if (rawStatus === 'scheduled' || rawStatus === 'agendado') {
          statusLabel = 'Agendado';
          statusBadgeClass = 'bg-blue-500/20 text-blue-300 border-blue-500/30';
          statusDotClass = 'bg-blue-400';
        } else if (rawStatus === 'running' || rawStatus === 'em_andamento' || rawStatus === 'in_progress') {
          statusLabel = 'Em Andamento';
          statusBadgeClass = 'bg-amber-500/20 text-amber-300 border-amber-500/30';
          statusDotClass = 'bg-amber-400 animate-pulse';
        } else if (rawStatus === 'finished' || rawStatus === 'finalizado' || rawStatus === 'concluido') {
          statusLabel = 'Finalizado';
          statusBadgeClass = 'bg-slate-500/20 text-slate-400 border-slate-500/30';
          statusDotClass = 'bg-slate-400';
        } else if (rawStatus === 'cancelled' || rawStatus === 'canceled' || rawStatus === 'cancelado') {
          statusLabel = 'Cancelado';
          statusBadgeClass = 'bg-rose-500/20 text-rose-300 border-rose-500/30';
          statusDotClass = 'bg-rose-400';
        } else if (rawStatus === 'open' || rawStatus === 'aberto') {
          statusLabel = 'Inscrições Abertas';
          statusBadgeClass = 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
          statusDotClass = 'bg-emerald-400';
        }

        return {
          id: t.id,
          name: t.name,
          game: t.games?.name,
          gameId: t.cardgame_id,
          ticket: t.ticket,
          rawDate: t.start_date,
          date: new Date(t.start_date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
          location: hostStore ? hostStore.name : 'Local da Loja',
          hostStoreId: hostStore ? (hostStore.slug || hostStore.id) : null,
          ticketsAvailable,
          totalSpots: t.max_players || 0,
          filledSpots: 0,
          rawStatus,
          status: statusLabel,
          statusLabel,
          statusBadgeClass,
          statusDotClass,
          imageUrl: t.image_url || 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=600'
        };
      });
      setTorneios(mapped);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTourneys();
  }, []);

  const filteredTournaments = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTime = today.getTime();

    return (torneios || []).filter(t => {
      if (!t) return false;
      
      // Game Filter
      if (activeGame !== 'All' && Number(t.gameId) !== Number(activeGame)) {
        return false;
      }
      
      const tourDate = new Date(t.rawDate);
      
      // Time Scope Filter (Upcoming vs All)
      if (timeScope === 'upcoming') {
        if (tourDate.getTime() < todayTime) return false;
      }
      
      // Month Filter
      if (selectedMonth !== 'All' && tourDate.getMonth() !== Number(selectedMonth)) {
        return false;
      }
      
      return true;
    }).sort((a, b) => new Date(a.rawDate).getTime() - new Date(b.rawDate).getTime());
  }, [torneios, activeGame, timeScope, selectedMonth]);

  const tournamentsStats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTime = today.getTime();

    const total = torneios.length;
    const upcoming = torneios.filter(t => new Date(t.rawDate).getTime() >= todayTime).length;
    const openNow = torneios.filter(t => t.rawStatus === 'open' || t.status === 'Aberto' || t.statusLabel === 'Aberto').length;
    const gameCount = new Set(torneios.map(t => t.gameId).filter(Boolean)).size || dbGames.length;

    return {
      total,
      upcoming,
      openNow,
      gameCount
    };
  }, [torneios, dbGames]);

  return (
    <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 animate-in fade-in duration-500 px-2 sm:px-4 pb-16">
      {/* Header Principal Padronizado */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 border-b border-slate-800/80 pb-4 sm:pb-6">
        <div>
          <div className="flex items-center space-x-3 mb-1">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white shadow-lg shadow-orange-500/20 shrink-0">
              <i className="fas fa-trophy text-base sm:text-lg"></i>
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-black text-white tracking-tight">
                Torneios & Eventos
              </h1>
              <p className="text-[11px] sm:text-xs md:text-sm text-slate-400">
                Encontre competições de TCG e participe dos eventos oficiais e da comunidade.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards de Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
        <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center space-x-2.5 sm:space-x-4 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-600/10 border border-purple-500/20 flex items-center justify-center text-purple-400 text-base sm:text-xl shrink-0">
            <i className="fas fa-trophy"></i>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-500 truncate">Total Torneios</p>
            <p className="text-lg sm:text-2xl font-black text-white">{tournamentsStats.total}</p>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center space-x-2.5 sm:space-x-4 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center text-blue-400 text-base sm:text-xl shrink-0">
            <i className="fas fa-calendar-check"></i>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-500 truncate">Próximos</p>
            <p className="text-lg sm:text-2xl font-black text-blue-400">{tournamentsStats.upcoming}</p>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center space-x-2.5 sm:space-x-4 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-emerald-600/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 text-base sm:text-xl shrink-0">
            <i className="fas fa-door-open"></i>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-500 truncate">Inscrições Abertas</p>
            <p className="text-lg sm:text-2xl font-black text-emerald-400">{tournamentsStats.openNow}</p>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center space-x-2.5 sm:space-x-4 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-amber-600/10 border border-amber-500/20 flex items-center justify-center text-amber-400 text-base sm:text-xl shrink-0">
            <i className="fas fa-shapes"></i>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-500 truncate">Card Games</p>
            <p className="text-lg sm:text-2xl font-black text-amber-400">{tournamentsStats.gameCount}</p>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3 sm:gap-4 bg-slate-900/40 p-3 sm:p-4 rounded-2xl border border-slate-800/80">
        <div className="flex items-center space-x-2">
          <span className="text-xs font-bold text-slate-400">Filtrando:</span>
          <span className="text-xs font-mono font-bold text-purple-400 bg-purple-950/50 px-2.5 py-1 rounded-lg border border-purple-500/20">
            {filteredTournaments.length} torneios encontrados
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3 w-full xl:w-auto">
          {/* Game filter */}
          <div className="relative group min-w-[180px]">
            <select 
              id="game-select"
              value={activeGame}
              onChange={(e) => {
                const val = e.target.value;
                setActiveGame(val === 'All' ? 'All' : Number(val));
              }}
              className="w-full bg-slate-950/80 border border-slate-800 hover:border-slate-700 text-white text-xs rounded-xl px-3.5 py-2.5 cursor-pointer appearance-none focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 transition-all font-bold uppercase tracking-wider pr-8"
            >
              <option value="All">TODOS OS JOGOS</option>
              {dbGames.map(game => (
                <option key={game.id} value={game.id}>
                  {game.name.toUpperCase()}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500 group-focus-within:text-purple-400 transition-colors">
              <i className="fas fa-chevron-down text-[10px]"></i>
            </div>
          </div>

          {/* Time Scope filter */}
          <div className="relative group min-w-[180px]">
            <select 
              id="scope-select"
              value={timeScope}
              onChange={(e) => {
                setTimeScope(e.target.value as any);
              }}
              className="w-full bg-slate-950/80 border border-slate-800 hover:border-slate-700 text-white text-xs rounded-xl px-3.5 py-2.5 cursor-pointer appearance-none focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 transition-all font-bold uppercase tracking-wider pr-8"
            >
              <option value="upcoming">PRÓXIMOS EVENTOS</option>
              <option value="all">TODOS OS EVENTOS</option>
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500 group-focus-within:text-purple-400 transition-colors">
              <i className="fas fa-chevron-down text-[10px]"></i>
            </div>
          </div>

          {/* Month filter */}
          <div className="relative group min-w-[180px]">
            <select 
              id="month-select"
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
              }}
              className="w-full bg-slate-950/80 border border-slate-800 hover:border-slate-700 text-white text-xs rounded-xl px-3.5 py-2.5 cursor-pointer appearance-none focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 transition-all font-bold uppercase tracking-wider pr-8"
            >
              <option value="All">QUALQUER MÊS</option>
              {MONTHS_PT.map(m => (
                <option key={m.value} value={m.value}>
                  {m.label.toUpperCase()}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500 group-focus-within:text-purple-400 transition-colors">
              <i className="fas fa-chevron-down text-[10px]"></i>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
        </div>
      ) : filteredTournaments.length === 0 ? (
        <div className="text-center py-20 bg-slate-900/20 border border-dashed border-slate-800 rounded-3xl">
          <i className="fas fa-calendar-times text-4xl text-slate-700 mb-4"></i>
          <h3 className="text-xl font-bold text-white">Nenhum evento encontrado</h3>
          <p className="text-slate-500 mt-2 text-sm">Tente mudar o filtro ou volte mais tarde.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTournaments.map((tourney) => (
            <div
              key={tourney.id}
              className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden group hover:border-purple-500/40 transition-colors flex flex-col"
            >
              <div className="relative h-48 overflow-hidden bg-slate-950">
                <img 
                  src={tourney.imageUrl} 
                  className="w-full h-full object-cover" 
                  alt={tourney.name} 
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent"></div>
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className="px-3 py-1 bg-purple-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg">
                    {tourney.game}
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-lg border flex items-center gap-1.5 bg-slate-950/90 ${tourney.statusBadgeClass}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${tourney.statusDotClass}`}></span>
                    {tourney.statusLabel}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-white">
                    <i className="fas fa-calendar-day text-xs text-purple-400"></i>
                    <span className="text-xs font-bold">{tourney.date}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-white">
                    <i className="fas fa-users text-xs text-purple-400"></i>
                    <span className="text-xs font-bold">{tourney.totalSpots ? `Max: ${tourney.totalSpots}` : 'Aberto'}</span>
                  </div>
                </div>
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-purple-400 transition-colors line-clamp-1">
                  {tourney.name}
                </h3>
                <div className="flex items-center space-x-2 text-slate-400 mb-6">
                  <i className="fas fa-store text-xs text-purple-400"></i>
                  {tourney.hostStoreId ? (
                    <Link 
                      to={`/loja/${tourney.hostStoreId}`}
                      className="text-xs font-bold text-slate-300 hover:text-purple-400 hover:underline transition-colors line-clamp-1"
                    >
                      {tourney.location}
                    </Link>
                  ) : (
                    <span className="text-xs line-clamp-1">{tourney.location}</span>
                  )}
                </div>

                <div className="mt-auto pt-4 border-t border-slate-800 flex items-center justify-between gap-2">
                  <Link 
                    to={`/evento/${tourney.id}`}
                    className="flex-1 text-center px-4 py-2.5 bg-slate-800 hover:bg-purple-600 text-white text-xs font-bold rounded-xl transition-colors border border-slate-700/50"
                  >
                    Ver Detalhes
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
};

